/**
 * What fits on what.
 *
 * Two separate questions, answered separately because they fail differently:
 *
 *  1. **Does this weapon have this slot at all?** A weapon with no handguard has
 *     nowhere to hang a foregrip; a bullpup's stock is the receiver. This is a
 *     property of the physical weapon and is answered by `slotsFor`.
 *
 *  2. **Does this particular part fit this particular weapon?** A choke belongs on a
 *     shotgun, a full-auto sear cannot be fitted to a bolt action, a belt box needs
 *     a weapon that feeds from a belt. Answered by `fits`.
 *
 * Both are enforced in `WeaponLoadouts.equip`, not in the gunsmith screen. A rule
 * that only exists in the UI is not a rule — it is a suggestion that survives until
 * the first save file someone edits, the first console call, or the first time the
 * screen is rebuilt by someone who did not know the rule was there.
 *
 * ---------------------------------------------------------------------------
 * MODEL_TRAITS
 * ---------------------------------------------------------------------------
 * Which slots a weapon has is decided by what it physically *is*, and the answer to
 * that lives in `models/specs.js` — a file this module deliberately does not import.
 * That file belongs to the model build and is under active construction; importing
 * it would couple the gunsmith's rules to a shape that is still moving, and a change
 * to a mesh spec would silently change which attachments a weapon can take.
 *
 * So the handful of facts that matter are recorded here instead, read off
 * `models/specs.js` on 2026-08-08 and cited the same way `weapons.stats.js` cites
 * the reference site. If a model gains a handguard later, this table is the one
 * place that needs to know.
 */

import { WEAPONS } from '../weapons.data.js';
import { SLOT_ORDER } from './schema.js';

/**
 * Per weapon: the furniture that decides slot availability.
 *
 *  `stock`      the stock style from its model spec. `'none'` is no stock at all;
 *               `'bullpup'` and `'thumbhole'` are integral to the receiver or the
 *               chassis and cannot be swapped for a different one.
 *  `handguard`  false where the spec says `style: 'none'` — an exposed barrel with
 *               nothing to mount under.
 *  `cheek`      the model carries a raised cheek piece, so a comb can replace it.
 *  `bipod`      the model deploys a bipod. Recorded for the behaviour agent; no slot
 *               currently keys off it.
 */
export const MODEL_TRAITS = {
  ak27: { stock: 'ak', handguard: true, cheek: false, bipod: false },
  x9maverick: { stock: 'collapsible', handguard: true, cheek: false, bipod: false },
  ds20mirage: { stock: 'bullpup', handguard: true, cheek: false, bipod: false },
  peacekeepermk1: { stock: 'folding', handguard: true, cheek: true, bipod: false },
  m15mod0: { stock: 'fixedTube', handguard: true, cheek: false, bipod: false },
  mxr17: { stock: 'skeleton', handguard: true, cheek: true, bipod: false },

  razor9mm: { stock: 'wire', handguard: true, cheek: false, bipod: false },
  mpc25: { stock: 'folding', handguard: true, cheek: false, bipod: false },
  rk9: { stock: 'wire', handguard: false, cheek: false, bipod: false },
  dravec45: { stock: 'folding', handguard: true, cheek: false, bipod: false },
  ryden45k: { stock: 'wood', handguard: false, cheek: false, bipod: false },
  carbon57: { stock: 'none', handguard: false, cheek: false, bipod: false },

  m10breacher: { stock: 'folding', handguard: true, cheek: false, bipod: false },
  echo12: { stock: 'skeleton', handguard: true, cheek: false, bipod: false },
  akita: { stock: 'wood', handguard: false, cheek: false, bipod: false },

  xm325: { stock: 'skeleton', handguard: true, cheek: false, bipod: true },
  mk78: { stock: 'ak', handguard: true, cheek: false, bipod: true },

  warden308: { stock: 'skeleton', handguard: true, cheek: true, bipod: false },
  m8a1: { stock: 'wood', handguard: true, cheek: false, bipod: false },
  m34novaline: { stock: 'bullpup', handguard: true, cheek: false, bipod: true },

  vsrecon: { stock: 'thumbhole', handguard: true, cheek: false, bipod: false },
  shadowsk: { stock: 'folding', handguard: true, cheek: true, bipod: true },
  xr3ion: { stock: 'bullpup', handguard: true, cheek: false, bipod: true },
};

/** Stock styles that are part of the receiver and cannot be exchanged. */
const INTEGRAL_STOCKS = new Set(['none', 'bullpup', 'thumbhole']);

/** Stock styles solid enough at the shoulder to take a butt pad. */
const PAD_STOCKS = new Set(['ak', 'wood', 'fixedTube', 'thumbhole']);

/** Classes whose recoil makes a butt pad worth fitting whatever the stock is. */
const PAD_CLASSES = new Set(['lmg', 'sg', 'mr', 'sr']);

/** Classes that are aimed through glass often enough to want a comb. */
const COMB_CLASSES = new Set(['mr', 'sr']);

/**
 * Which of the eleven slots this weapon actually has.
 *
 * Returns a `Set`. Everything downstream asks this rather than assuming eleven.
 */
export function slotsFor(weaponId) {
  const def = WEAPONS[weaponId];
  if (!def) return new Set();
  const t = MODEL_TRAITS[weaponId] ?? { stock: 'collapsible', handguard: true, cheek: false };
  const cls = def.classId;
  const out = new Set(['optic', 'muzzle', 'barrel', 'rearGrip', 'laser', 'fireMod']);

  // Nowhere to clamp a foregrip, a handstop or a launcher tube.
  if (t.handguard) out.add('underbarrel');

  // A bullpup's "stock" is the back half of the receiver, a thumbhole is one piece
  // of timber, and the Carbon 57 does not have one. None of the three can be swapped
  // for a different stock — but the first two still have a shoulder to pad.
  if (!INTEGRAL_STOCKS.has(t.stock)) out.add('stock');
  if (PAD_CLASSES.has(cls) || PAD_STOCKS.has(t.stock)) out.add('stockPad');
  if (t.cheek || COMB_CLASSES.has(cls)) out.add('comb');

  // Shell-by-shell means a tube under the barrel, not a magazine well. Read off the
  // weapon's own reload behaviour rather than off the model, because the *reload* is
  // what a magazine attachment would have to change.
  if (!def.shellByShell) out.add('magazine');

  return out;
}

/** The same answer, ordered, for the gunsmith screen. */
export function slotListFor(weaponId) {
  const s = slotsFor(weaponId);
  return SLOT_ORDER.filter((x) => s.has(x));
}

/**
 * Does this attachment fit this weapon?
 *
 * `fit` is read as: the weapon must pass every clause that is present.
 *
 *   `classes`      allowed classes. A weapon named in `weapons` is allowed too —
 *                  the two are a union, which is how the belt box reaches the one
 *                  belt-fed submachine gun without pretending it is an LMG.
 *   `notClasses`   forbidden classes.
 *   `weapons` /    explicit allow / deny lists, by weapon id.
 *   `notWeapons`
 *   `fireModes`    the weapon's **base** fire mode must be one of these. Read off
 *                  the base table, not off the resolved build: whether a full-auto
 *                  sear fits an M8A1 is a fact about the M8A1, and must not depend
 *                  on which other attachment happens to be fitted at the time — that
 *                  way lies a build that becomes illegal when you remove a part from
 *                  a different slot.
 *   `notFireModes`
 */
export function fits(att, weaponId) {
  const def = WEAPONS[weaponId];
  if (!def || !att) return false;
  if (!slotsFor(weaponId).has(att.slot)) return false;

  const f = att.fit;
  if (!f) return true;
  const cls = def.classId;

  if (f.notWeapons?.includes(weaponId)) return false;
  if (f.notClasses?.includes(cls)) return false;
  if (f.classes || f.weapons) {
    const byClass = f.classes?.includes(cls) ?? false;
    const byWeapon = f.weapons?.includes(weaponId) ?? false;
    if (!byClass && !byWeapon) return false;
  }
  if (f.fireModes && !f.fireModes.includes(def.fireMode)) return false;
  if (f.notFireModes?.includes(def.fireMode)) return false;
  return true;
}

/* -------------------------------------------------------------------------- */
/* optic mount groups                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Which weapons share a sight mount, and therefore share an optic unlock.
 *
 * An optic earned on one weapon is available on every weapon in the same group
 * immediately. The grouping is by what the mount physically is, so the rule is
 * legible: a rifle rail is a rifle rail, and a sight that fits one fits them all.
 *
 * Three groups rather than one, because a 7x on a rail-standard weapon is not a
 * thing the optic slot should ever have to reason about — the fit rules already say
 * so, and the shared-unlock groups agreeing with them keeps the two from drifting.
 */
export const OPTIC_GROUPS = {
  /** Flat-top rails: rifles, submachine guns, machine guns. */
  'rail-standard': ['ar', 'smg', 'lmg'],
  /** Ribbed and bead-sighted shotgun receivers. */
  'rail-shotgun': ['sg'],
  /** Long precision rails with the eye relief for magnified glass. */
  'rail-precision': ['mr', 'sr'],
};

const GROUP_BY_CLASS = {};
for (const [group, classes] of Object.entries(OPTIC_GROUPS)) {
  for (const c of classes) GROUP_BY_CLASS[c] = group;
}

/** The optic mount group a weapon belongs to. */
export function opticGroupFor(weaponId) {
  const def = WEAPONS[weaponId];
  return def ? GROUP_BY_CLASS[def.classId] ?? 'rail-standard' : 'rail-standard';
}

/** Every weapon in the same optic mount group, including the one asked about. */
export function opticGroupPeers(weaponId) {
  const g = opticGroupFor(weaponId);
  return Object.keys(WEAPONS).filter((id) => opticGroupFor(id) === g);
}
